import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../../components/ui/dialog';
import {
  Loader2, Search, Send, Tag, MessageSquare, User, Bot, Headphones, Building2,
  RefreshCw, PhoneOutgoing, Hand, Undo2, ClipboardList, ArrowLeft,
  Paperclip, Clock,
} from 'lucide-react';
import { CRMComposer, ETIQUETAS_HABILITAN_COMPOSER } from './CRMComposer';
import { PlantillaBadge, PlantillaDetalle, VentanaCerradaNota } from './CRMHiloExtras';
import { CRMContactoItem } from './CRMContactoItem';
import { CRMBulkBar } from './CRMBulkBar';
import { CRMSalirDialog, ETIQUETAS_SIN_PREGUNTA } from './CRMSalirDialog';
import { ResponsableSelector } from './CRMResponsable';
import { CRMArchivoBurbuja } from './CRMArchivoBurbuja';
import { CRMTakeoverMotivo } from './CRMTakeoverMotivo';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CRM_BASE = `${API_URL}/api/webhooks/twilio/admin`;

const ETIQUETAS = [
  { id: 'bot', label: 'Bot', color: 'bg-slate-100 text-slate-500' },
  { id: 'esperando', label: 'Esperando', color: 'bg-orange-200 text-orange-800' },
  { id: 'en_curso', label: 'En curso', color: 'bg-sky-200 text-sky-800' },
  { id: 'cerrada', label: 'Cerrada', color: 'bg-emerald-200 text-emerald-800' },
];
const etiquetaMeta = (id) => ETIQUETAS.find(e => e.id === id) || ETIQUETAS[0];

const fuenteIcon = (fuente, direccion) => {
  if (fuente === 'sellos' || fuente === 'auxiliares') return <Building2 className="w-3 h-3" />;
  if (fuente === 'takeover') return <Headphones className="w-3 h-3" />;
  if (fuente === 'solicitud') return <ClipboardList className="w-3 h-3" />;
  if (direccion === 'in') return <User className="w-3 h-3" />;
  return <Bot className="w-3 h-3" />;
};

export default function WhatsAppCRMTab({ token, filtroUrl, filtroKey }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [contactos, setContactos] = useState([]);
  const [loadingContactos, setLoadingContactos] = useState(true);
  const [filtroQ, setFiltroQ] = useState('');
  const [filtroEtiqueta, setFiltroEtiqueta] = useState(filtroUrl === 'atencion' ? 'esperando' : '');
  const soloNoLeidosInicial = filtroUrl === 'no_leidos';
  const reqContactos = useRef(0);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [conv, setConv] = useState(null);
  const [loadingConv, setLoadingConv] = useState(false);
  const [sending, setSending] = useState(false);
  const [accion, setAccion] = useState(null);
  const [showIniciar, setShowIniciar] = useState(false);
  const [iniciarForm, setIniciarForm] = useState({ nombre: '', mensaje: '' });
  const scrollRef = useRef(null);
  const [soloNoLeidos, setSoloNoLeidos] = useState(soloNoLeidosInicial);
  const [misConv, setMisConv] = useState(false);
  const [soloArchivos, setSoloArchivos] = useState(filtroUrl === 'archivos');
  const [soloDemoradas, setSoloDemoradas] = useState(filtroUrl === 'demoradas');
  const [equipo, setEquipo] = useState([]);
  const [seleccionando, setSeleccionando] = useState(false);
  const [seleccionados, setSeleccionados] = useState([]);
  const [salir, setSalir] = useState(null);
  const abiertoConNoLeidos = useRef(false);

  const cargarContactos = useCallback(async (silencioso = false) => {
    if (!silencioso) setLoadingContactos(true);
    const reqId = ++reqContactos.current;
    try {
      const r = await axios.get(`${CRM_BASE}/contactos`, {
        headers, params: { q: filtroQ, etiqueta: filtroEtiqueta, dias: 30, solo_no_leidos: soloNoLeidos, mis: misConv, solo_archivos_pendientes: soloArchivos, solo_demoradas: soloDemoradas },
      });
      if (reqId === reqContactos.current) setContactos(r.data.items || []);
    } catch (e) { if (!silencioso) toast.error('Error cargando contactos'); }
    if (!silencioso) setLoadingContactos(false);
  }, [headers, filtroQ, filtroEtiqueta, soloNoLeidos, misConv, soloArchivos, soloDemoradas]);

  useEffect(() => {
    axios.get(`${CRM_BASE}/crm/equipo`, { headers }).then(r => setEquipo(r.data.items || [])).catch(() => {});
  }, [headers]);

  const asignarResponsable = async (phone, userId) => {
    try {
      const r = await axios.put(`${CRM_BASE}/sesiones/${encodeURIComponent(phone)}/responsable`, { user_id: userId || null }, { headers });
      const resp = r.data.responsable ? { user_id: r.data.responsable.id, nombre: r.data.responsable.nombre || r.data.responsable.email } : null;
      setConv(prev => (prev ? { ...prev, responsable: resp } : prev));
      setContactos(prev => prev.map(c => (c.phone === phone ? { ...c, responsable: resp } : c)));
      toast.success(resp ? `Asignada a ${resp.nombre}` : 'Sin responsable');
      if (misConv) cargarContactos(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo asignar'); }
  };

  const cargarConversacion = useCallback(async (phone, silencioso = false) => {
    if (!phone) return;
    if (!silencioso) setLoadingConv(true);
    try {
      const r = await axios.get(`${CRM_BASE}/conversacion/${encodeURIComponent(phone)}`, { headers, params: { marcar_leido: true } });
      setConv(prev => {
        const nuevos = !prev || prev.phone !== r.data.phone || prev.total !== r.data.total;
        if (nuevos) setTimeout(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }); }, 100);
        return r.data;
      });
    } catch (e) { if (!silencioso) toast.error('Error cargando conversación'); }
    if (!silencioso) setLoadingConv(false);
  }, [headers]);

  useEffect(() => {
    if (!['atencion', 'no_leidos', 'archivos', 'demoradas'].includes(filtroUrl)) return;
    setFiltroEtiqueta(filtroUrl === 'atencion' ? 'esperando' : '');
    setSoloNoLeidos(filtroUrl === 'no_leidos');
    setSoloArchivos(filtroUrl === 'archivos');
    setSoloDemoradas(filtroUrl === 'demoradas');
    setMisConv(false); setFiltroQ('');
  }, [filtroUrl, filtroKey]);
  useEffect(() => { cargarContactos(); }, [cargarContactos]);
  useEffect(() => {
    const t = setInterval(() => cargarContactos(true), 20000);
    return () => clearInterval(t);
  }, [cargarContactos]);
  useEffect(() => { if (selectedPhone) cargarConversacion(selectedPhone); }, [selectedPhone, cargarConversacion]);
  useEffect(() => {
    if (!selectedPhone) return undefined;
    const t = setInterval(() => cargarConversacion(selectedPhone, true), 10000);
    return () => clearInterval(t);
  }, [selectedPhone, cargarConversacion]);

  const seleccionarEtiqueta = async (phone, etiqueta) => {
    try {
      await axios.put(`${CRM_BASE}/sesiones/${encodeURIComponent(phone)}/etiqueta`,
        { etiqueta }, { headers });
      toast.success('Etiqueta actualizada');
      setContactos(prev => prev.map(c => c.phone === phone ? { ...c, etiqueta_crm: etiqueta } : c));
      if (conv?.phone === phone) setConv({ ...conv, etiqueta_crm: etiqueta });
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const irA = (phone) => {
    const c = contactos.find(x => x.phone === phone);
    abiertoConNoLeidos.current = !!c?.no_leidos;
    if (phone) setContactos(prev => prev.map(x => x.phone === phone ? { ...x, no_leidos: 0 } : x));
    setSelectedPhone(phone);
    if (!phone) setConv(null);
  };

  const abrirContacto = (phone) => {
    if (phone === selectedPhone) return;
    if (selectedPhone && abiertoConNoLeidos.current && !ETIQUETAS_SIN_PREGUNTA.includes(conv?.etiqueta_crm)) {
      setSalir({ phone: selectedPhone, next: phone });
      return;
    }
    irA(phone);
  };

  const resolverSalir = async (pasar) => {
    const { phone, next } = salir;
    setSalir(null);
    if (pasar) await seleccionarEtiqueta(phone, 'en_curso');
    irA(next);
  };

  const toggleSel = (phone) => setSeleccionados(prev => prev.includes(phone) ? prev.filter(p => p !== phone) : [...prev, phone]);
  const toggleTodos = () => setSeleccionados(prev => prev.length === contactos.length ? [] : contactos.map(c => c.phone));

  const aplicarMasivo = async (payload) => {
    try {
      await axios.post(`${CRM_BASE}/sesiones/masivo`, { phones: seleccionados, ...payload }, { headers });
      toast.success(`${seleccionados.length} conversación(es) actualizada(s)`);
      if (selectedPhone && seleccionados.includes(selectedPhone)) cargarConversacion(selectedPhone, true);
      setSeleccionados([]);
      await cargarContactos(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo actualizar'); }
  };

  const iniciarConversacion = async () => {
    if (!iniciarForm.mensaje.trim()) { toast.error('El mensaje es obligatorio'); return; }
    setSending(true);
    try {
      const r = await axios.post(`${CRM_BASE}/sesiones/${encodeURIComponent(selectedPhone)}/iniciar`,
        iniciarForm, { headers });
      toast.success(r.data?.mensaje || 'Mensaje enviado');
      setShowIniciar(false);
      setIniciarForm({ nombre: '', mensaje: '' });
      await cargarConversacion(selectedPhone);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo enviar');
    }
    setSending(false);
  };

  const enviarMensaje = async (mensaje) => {
    try {
      await axios.post(`${CRM_BASE}/sesiones/${encodeURIComponent(selectedPhone)}/responder`, { mensaje }, { headers });
      await cargarConversacion(selectedPhone, true);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo enviar');
      return false;
    }
  };

  const abrirPlantilla = () => {
    setIniciarForm({ nombre: '', mensaje: '' });
    setShowIniciar(true);
  };

  const cambiarControl = async (tipo) => {
    setAccion(tipo);
    try {
      const ruta = tipo === 'tomar' ? 'tomar-control' : 'devolver-bot';
      const r = await axios.post(`${CRM_BASE}/sesiones/${encodeURIComponent(selectedPhone)}/${ruta}`, {}, { headers });
      const etiqueta = r.data?.etiqueta;
      toast.success(tipo === 'tomar' ? 'Tomaste el control de la conversación'
        : `Conversación devuelta al bot${r.data?.aviso_enviado ? ' · se avisó al contacto' : ''}`);
      setContactos(prev => prev.map(c => c.phone === selectedPhone ? { ...c, etiqueta_crm: etiqueta } : c));
      await cargarConversacion(selectedPhone, true);
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo completar la acción'); }
    setAccion(null);
  };

  const fmt = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
  };

  return (
    <div className="h-[calc(100dvh-200px)] md:h-[calc(100vh-220px)] flex gap-3 min-h-[460px]" data-testid="whatsapp-crm">
      {/* Panel izquierdo · lista de contactos */}
      <div className={`${selectedPhone ? 'hidden md:flex' : 'flex'} w-full md:w-80 border rounded-lg bg-white flex-col shrink-0 min-w-0`}
        data-testid="crm-panel-contactos">
        <div className="p-3 border-b space-y-2">
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-gray-500" />
            <Input placeholder="Buscar por nombre o teléfono..." value={filtroQ}
              onChange={e => setFiltroQ(e.target.value)}
              className="h-8 text-sm" data-testid="crm-search" />
            <Button size="sm" variant="ghost" onClick={() => cargarContactos()} title="Recargar" data-testid="crm-btn-recargar">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <select
              className="basis-full min-w-0 border rounded-md px-2 py-1 text-xs bg-white"
              value={filtroEtiqueta}
              onChange={e => setFiltroEtiqueta(e.target.value)}
              data-testid="crm-filter-etiqueta"
            >
              <option value="">Todas las etiquetas</option>
              {ETIQUETAS.map(e => <option key={e.id} value={e.id}>{e.label}</option>)}
            </select>
            <Button size="sm" variant={soloNoLeidos ? 'default' : 'outline'}
              className={`h-7 text-xs px-2 ${soloNoLeidos ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
              onClick={() => setSoloNoLeidos(v => !v)} data-testid="crm-filter-no-leidos">No leídos</Button>
            <Button size="sm" variant={misConv ? 'default' : 'outline'}
              className={`h-7 text-xs px-2 ${misConv ? 'bg-sky-600 hover:bg-sky-700' : ''}`}
              onClick={() => setMisConv(v => !v)} data-testid="crm-filter-mis">Mías</Button>
            <Button size="sm" variant={soloDemoradas ? 'default' : 'outline'} title="Esperando hace más de 30 min (en horario de atención)"
              className={`h-7 text-xs px-2 ${soloDemoradas ? 'bg-red-600 hover:bg-red-700' : ''}`}
              onClick={() => setSoloDemoradas(v => !v)} data-testid="crm-filter-demoradas"><Clock className="w-3.5 h-3.5" /></Button>
            <Button size="sm" variant={soloArchivos ? 'default' : 'outline'} title="Con archivos pendientes de revisión"
              className={`h-7 text-xs px-2 ${soloArchivos ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
              onClick={() => setSoloArchivos(v => !v)} data-testid="crm-filter-archivos"><Paperclip className="w-3.5 h-3.5" /></Button>
            <Button size="sm" variant={seleccionando ? 'default' : 'outline'} className="h-7 text-xs px-2"
              onClick={() => { setSeleccionando(v => !v); setSeleccionados([]); }} data-testid="crm-btn-seleccionar">
              {seleccionando ? 'Cancelar' : 'Seleccionar'}
            </Button>
          </div>
          <p className="text-xs text-gray-500">{contactos.length} contacto{contactos.length !== 1 ? 's' : ''}</p>
        </div>
        {seleccionando && (
          <CRMBulkBar etiquetas={ETIQUETAS} equipo={equipo} total={contactos.length} seleccionados={seleccionados}
            onToggleTodos={toggleTodos} onAplicar={aplicarMasivo} />
        )}
        <div className="flex-1 overflow-y-auto">
          {loadingContactos ? (
            <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-amber-600" /></div>
          ) : contactos.length === 0 ? (
            <p className="p-6 text-center text-gray-500 text-sm">Sin contactos</p>
          ) : contactos.map(c => (
            <CRMContactoItem key={c.phone} c={c} meta={etiquetaMeta(c.etiqueta_crm)} active={c.phone === selectedPhone}
              seleccionando={seleccionando} seleccionado={seleccionados.includes(c.phone)}
              onToggle={toggleSel} onOpen={abrirContacto} fecha={fmt(c.fecha)} />
          ))}
        </div>
      </div>

      {/* Panel derecho · hilo */}
      <div className={`${selectedPhone ? 'flex' : 'hidden md:flex'} flex-1 border rounded-lg bg-slate-50 flex-col min-w-0`}
        data-testid="crm-panel-hilo">
        {!selectedPhone ? (
          <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
            <div className="text-center">
              <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-30" />
              Elegí un contacto para ver el hilo
            </div>
          </div>
        ) : (
          <>
            {/* Header: nombre + etiqueta selector */}
            <div className="p-3 border-b bg-white flex items-center justify-between gap-2 flex-wrap">
              <div className="min-w-0 flex items-center gap-2">
                <Button size="sm" variant="ghost" className="md:hidden h-8 w-8 p-0 shrink-0" onClick={() => abrirContacto(null)}
                  title="Volver a contactos" data-testid="crm-btn-volver">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <div className="min-w-0">
                <p className="font-semibold text-sm truncate">
                  {conv?.phone || selectedPhone}
                </p>
                <p className="text-xs text-gray-500">{conv?.total || 0} mensajes</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button size="sm" variant="outline" className="h-8 text-xs border-purple-300 text-purple-800 hover:bg-purple-50"
                  onClick={() => cambiarControl('tomar')} disabled={!!accion} data-testid="crm-btn-tomar-control">
                  {accion === 'tomar' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Hand className="w-3.5 h-3.5 mr-1" />}
                  Tomar control
                </Button>
                {(conv?.takeover_abierto || ETIQUETAS_HABILITAN_COMPOSER.includes(conv?.etiqueta_crm)) && (
                  <Button size="sm" variant="outline" className="h-8 text-xs border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                    onClick={() => cambiarControl('devolver')} disabled={!!accion} data-testid="crm-btn-devolver-bot">
                    {accion === 'devolver' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Undo2 className="w-3.5 h-3.5 mr-1" />}
                    Devolver al bot
                  </Button>
                )}
                <ResponsableSelector equipo={equipo} value={conv?.responsable?.user_id}
                  onChange={(id) => asignarResponsable(selectedPhone, id)} />
                <Tag className="w-4 h-4 text-gray-500" />
                <select
                  className="border rounded-md px-2 py-1 text-xs bg-white"
                  value={conv?.etiqueta_crm || 'bot'}
                  onChange={e => seleccionarEtiqueta(selectedPhone, e.target.value)}
                  data-testid="crm-etiqueta-selector"
                >
                  {ETIQUETAS.map(e => <option key={e.id} value={e.id}>{e.label}</option>)}
                </select>
              </div>
            </div>
            <CRMTakeoverMotivo tk={conv?.takeover_abierto} />

            {/* Hilo */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="crm-hilo">
              {loadingConv ? (
                <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin inline text-amber-600" /></div>
              ) : !conv?.mensajes?.length ? (
                <p className="text-center text-gray-400 text-sm py-12">Sin mensajes en este hilo</p>
              ) : conv.mensajes.map((m, i) => {
                if (m.fuente === 'archivo') {
                  return <CRMArchivoBurbuja key={`arch-${i}`} m={m} base={CRM_BASE} headers={headers} fecha={fmt(m.at)}
                    onRevisado={() => cargarConversacion(selectedPhone, true)} />;
                }
                const alignRight = m.direccion === 'out';
                const colorBubble = (
                  m.fuente === 'solicitud' && m.autor === 'sistema' ? 'bg-yellow-50 border border-yellow-300 text-yellow-900' :
                  m.fuente === 'solicitud' && m.direccion === 'out' ? 'bg-emerald-100 text-emerald-900' :
                  m.fuente === 'sellos' || m.fuente === 'auxiliares' ? 'bg-indigo-100 text-indigo-900' :
                  m.fuente === 'takeover' && m.direccion === 'out' ? 'bg-emerald-100 text-emerald-900' :
                  m.fuente === 'ia' ? 'bg-sky-100 text-sky-900' :
                  alignRight ? 'bg-amber-100 text-amber-900' : 'bg-white border'
                );
                const label = (
                  m.fuente === 'sellos' ? `Sellos${m.meta?.evento ? ' · ' + m.meta.evento : ''}` :
                  m.fuente === 'auxiliares' ? 'Auxiliares de Justicia' :
                  m.fuente === 'solicitud' ? (m.autor === 'sistema' ? `Solicitud${m.meta?.tipo ? ' · ' + m.meta.tipo.replace(/_/g, ' ') : ''}` : m.autor === 'agente' ? `Agente${m.meta?.agente ? ' · ' + m.meta.agente : ''}` : 'Usuario') :
                  m.fuente === 'takeover' ? (m.autor === 'agente' ? `Agente${m.meta?.agente ? ' · ' + m.meta.agente : ''}` : 'Usuario') :
                  m.fuente === 'ia' ? (m.autor === 'bot' ? 'IA' : 'Usuario') :
                  m.autor === 'bot' ? 'Bot' : 'Usuario'
                );
                return (
                  <div key={i} className={`flex ${alignRight ? 'justify-end' : 'justify-start'}`} data-testid={`crm-msg-${i}`}>
                    <div className={`${colorBubble} max-w-[85%] md:max-w-[75%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words`}>
                      <div className="flex items-center gap-1 text-[10px] opacity-70 mb-1">
                        {fuenteIcon(m.fuente, m.direccion)} {label}
                      </div>
                      {m.meta?.plantilla && <PlantillaBadge />}
                      {m.texto || <em className="opacity-50">(sin texto)</em>}
                      {m.meta?.plantilla && <PlantillaDetalle meta={m.meta} />}
                      <div className="text-[10px] opacity-50 text-right mt-1">{fmt(m.at)}</div>
                    </div>
                  </div>
                );
              })}
              {!loadingConv && conv?.mensajes?.length > 0 && !conv?.ventana_24h_abierta
                && ETIQUETAS_HABILITAN_COMPOSER.includes(conv?.etiqueta_crm) && (
                <VentanaCerradaNota automatica={conv.mensajes[conv.mensajes.length - 1]?.autor === 'modulo'}
                  onTemplate={abrirPlantilla} />
              )}
            </div>

            <CRMComposer key={selectedPhone} conv={{ ...conv, nombre: contactos.find(c => c.phone === selectedPhone)?.nombre }}
              onSend={enviarMensaje} base={CRM_BASE} headers={headers} />
          </>
        )}
      </div>

      <CRMSalirDialog open={!!salir} phone={salir?.phone} onConfirm={() => resolverSalir(true)} onSkip={() => resolverSalir(false)} />

      {/* Modal: iniciar conversación con template */}
      <Dialog open={showIniciar} onOpenChange={setShowIniciar}>
        <DialogContent data-testid="crm-dialog-iniciar" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PhoneOutgoing className="w-5 h-5 text-amber-600" /> Enviar WhatsApp
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-xs text-gray-600">
              Se enviará usando el template Twilio aprobado. Rellená las variables antes de confirmar.
            </p>
            <div>
              <label className="text-xs text-gray-700">Nombre (variable {'{{1}}'} del template)</label>
              <Input value={iniciarForm.nombre} onChange={e => setIniciarForm({ ...iniciarForm, nombre: e.target.value })}
                placeholder="Nombre del/la matriculade" data-testid="crm-iniciar-nombre" />
            </div>
            <div>
              <label className="text-xs text-gray-700">Mensaje (variable {'{{2}}'} del template)</label>
              <textarea className="w-full border rounded-md px-3 py-2 text-sm" rows={4}
                value={iniciarForm.mensaje}
                onChange={e => setIniciarForm({ ...iniciarForm, mensaje: e.target.value })}
                data-testid="crm-iniciar-mensaje" />
            </div>
            <p className="text-[11px] text-gray-500">
              Destino: <span className="font-mono">{selectedPhone}</span>
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIniciar(false)}>Cancelar</Button>
            <Button onClick={iniciarConversacion} disabled={sending || !iniciarForm.mensaje.trim()}
              data-testid="crm-iniciar-confirmar">
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
              Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
